import {
    assert,
    assertNever,
    DefaultMap,
    defined,
    definedMap,
    mapFilterUndefined,
    panic,
    proveNever,
} from "@glideapps/ts-necessities";
import { doesAuthenticationMethodAllowSignUp, getAppAuthenticationData } from "common/authentication-method";
import type { ChangeObservable } from "common/change-obserable";
import { Watchable } from "common/change-obserable";
import type { PrimitiveValue } from "common/components/computation-types";
import { Data } from "common/components/data";
import type { AppDescription } from "common/components/SerializedApp";
import { AppKind, getAppTabs, ScreenFlag } from "common/components/SerializedApp";
import type { AppEnvironment, AppFacilities, AppOther, ComputationModel, DataStore } from "common/components/types";
import type { GroundValue, Row } from "common/computation-model/data";
import { asMaybeString, asString, asTable, getRowColumn } from "common/computation-model/data";
import type { RowIndex } from "common/Database";
import { AuthenticationMethod } from "common/Database";
import type { TableName } from "common/database-strings";
import { logInfo } from "common/debug-print";
import type { BaseRowIndex } from "common/firebase-function-types";
import { isBaseRowIndex } from "common/firebase-function-types";
import type {
    ExistingAppDescriptionContext,
    InternalRowContext,
    InternalScreenContext,
    WireActionBackend,
    WireActionCallbacks,
    WireActionRunner,
    WireAlwaysEditableValue,
    WireComponentWithFlags,
    WireRowComponentHydrationBackend,
} from "common/generator/components";
import { PageScreenTarget } from "common/generator/components";
import type { WireButtonComponent } from "common/generator/components/fluent-components";
import type {
    ParsedModalScreen,
    ParsedPath,
    ParsedScreen,
    ParsedScreenBase,
} from "common/generator/components/parsed-path";
import { PageScreenPosition, WireModalSize } from "common/generator/components/parsed-path";
import { getScreenProperty } from "common/generator/components/property-utils";
import type {
    ArrayScreenDescription,
    ClassScreenDescription,
    InputOutputTables,
    ScreenDescription,
    TabDescription,
    TableGlideType,
} from "common/generator/description";
import {
    areTableNamesEqual,
    classScreenName,
    editClassScreenName,
    getTableName,
    getTableRefTableName,
    isAddClassScreenName,
    isClassOrArrayScreenDescription,
    isComputedColumn,
    isEditClassScreenName,
    isFormScreen,
    isPrimitiveType,
    isSingleRelationType,
    makeInputOutputTables,
    MutatingScreenKind,
    ScreenDescriptionKind,
    userProfileScreenName,
} from "common/generator/description";
import {
    findTabInAppDescription,
    makeInputOutputTablesForClassOrArrayScreen,
} from "common/generator/description-utils";
import { rowIndexColumnName } from "common/google-sheets";
import { makeRowID } from "common/make-row-id";
import { memoizeFunction } from "common/memoize";
import { isArray, nullToUndefined, removeNullFromObject, shallowEqualArrays } from "common/Support";
import { ArraySet } from "common/support/array-set";
import type {
    WireComponent,
    WireModalScreen,
    WireNavigationModel,
    WireScreen,
    WireTab,
    WireUserProfile,
} from "common/wire";
import { WireScreenFlag } from "common/wire";
import type { WireSignInComponent } from "common/wire/base-components";
import { WireComponentKind } from "common/wire/component-kind";
import type { WireAction } from "common/wire/types";
import deepEqual from "deep-equal";
import { getMutatingKindForScreen } from "lib/mutating-screen-kind";
import fromPairs from "lodash/fromPairs";
import zip from "lodash/zip";
import { shallowEqual } from "react-redux";
import { v4 as uuid } from "uuid";
import type { InternalTabHydrator } from "./hydration";
import { RowComponentHydrationBackend, RowHydrationValueProvider, TableComponentHydrationBackend } from "./hydration";
import { emptyInternalScreenContext, makeInternalScreenContext } from "./internal";
import type {
    ActionTokenMap,
    ComponentState,
    HydratedScreen,
    InternalScreen,
    InternalTab,
    OnChangeData,
    ScreenHydrationContext,
    StateValuesMap,
    SubscriptionInfo,
    ValueProviderActionContext,
} from "./internal-types";
import type { SpecialScreenName } from "./parsed-path";
import {
    getParsedScreens,
    isParsedPathValid,
    isSpecialScreenName,
    makeRootParsedPath,
    normalizeURLPath,
    parsedPathCloseModal,
    parsedPathPush,
    parsedPathRemoveModals,
    parseURLPath,
    unparseParsedPath,
    unparseParsedScreen,
} from "./parsed-path";
import type { ScreenInflatorCallbacks } from "./screen-inflator";
import { ScreenInflator } from "./screen-inflator";
import type { BuilderNavigationModel, NavigationModelData, WireBackend, WireBuilderBackend } from "./types";

function maybeShallowOldest(older: any, newer: any) {
    if (shallowEqual(older, newer)) {
        return older;
    }
    return newer;
}

function makeStateSaveKey(componentID: string | undefined, rowID: string | undefined): string | undefined {
    if (componentID === undefined) return undefined;
    return `${componentID}-${rowID}`;
}

function isSubscriptionDirty(si: SubscriptionInfo, parsedPathChanged: boolean): boolean {
    if (si.handlerAndPath?.handler.needsRedraw === true) return true;
    if (si.needsParsedPath && parsedPathChanged) return true;
    return si.subsidiaries.some(s => isSubscriptionDirty(s, parsedPathChanged));
}

function isComponentStateDirty(
    cs: ComponentState,
    additionalInputs: readonly unknown[],
    parsedPathChanged: boolean
): boolean {
    return (
        isSubscriptionDirty(cs, parsedPathChanged) ||
        cs.stateChanged ||
        !shallowEqualArrays(cs.additionalInputs, additionalInputs)
    );
}

interface HydratedModalScreen extends HydratedScreen {
    readonly wireScreen: WireModalScreen;
}

interface InternalModalScreen extends InternalScreen {
    readonly size: WireModalSize;
    readonly hydratedScreen: HydratedModalScreen | undefined;
}

interface InternalNavigationModel {
    readonly screen: InternalScreen;
    readonly modal: InternalModalScreen | undefined;

    readonly tabs: readonly InternalTab[] | undefined;

    readonly userProfile: WireUserProfile | undefined;
    readonly userProfileSubscriptionInfo: SubscriptionInfo | undefined;
}

const emptyScreen: WireScreen = {
    key: "empty",
    title: "",
    components: [],
    specialButtons: [],
    closeAction: undefined,
    flags: [],
};

// FIXME: This shouldn't be necessary anymore, since we're memoizing them,
// though that also includes the containing screen row.
function areScreenContextsEqual(a: InternalScreenContext, b: InternalScreenContext): boolean {
    if (a.inputRow !== b.inputRow) return false;
    if (a.outputRow !== b.outputRow) return false;
    return true;
}

function keepSubscriptionInfo<T extends SubscriptionInfo>(
    hydrationContext: ScreenHydrationContext,
    hydratedScreen: HydratedScreen,
    si: T
): T {
    assert(hydratedScreen !== undefined);

    for (const token of si.tokens) {
        if (hydratedScreen.actions.has(token)) {
            hydrationContext.actions.set(token, defined(hydratedScreen.actions.get(token)));
        } else if (hydratedScreen.onChangeData.has(token)) {
            hydrationContext.onChangeValueRows.set(token, defined(hydratedScreen.onChangeData.get(token)));
        } else {
            return panic("Token not registered");
        }
    }

    for (const subsidiary of si.subsidiaries) {
        keepSubscriptionInfo(hydrationContext, hydratedScreen, subsidiary);
    }

    return si;
}

interface ScreenHydrationResult {
    readonly hydratedScreen: HydratedScreen;
    readonly context: InternalScreenContext;
}

enum State {
    Clean,
    Requested,
    Recomputing,
    FollowUpRequested,
    FollowUpWithCleanupRequested,
    Retired,
}

function getRowIndexForRow(table: TableGlideType, row: Row): RowIndex | undefined {
    const maybeRowIndex = getRowColumn(row, rowIndexColumnName);
    let rowIndexHint: BaseRowIndex | undefined;
    if (isBaseRowIndex(maybeRowIndex)) {
        rowIndexHint = maybeRowIndex;
    }

    if (table.rowIDColumn !== undefined) {
        const rowID = getRowColumn(row, table.rowIDColumn);
        if (typeof rowID !== "string") return;

        return { keyColumnName: table.rowIDColumn, keyColumnValue: rowID, rowIndexHint };
    } else if (rowIndexHint !== undefined) {
        return rowIndexHint;
    } else {
        return undefined;
    }
}

interface WireBackendCallbacks extends WireActionCallbacks {
    // These are callbacks that could go to the frontend, but they're
    // synchronous, and not easily made asynchronous.  It's not clear we'll
    // actually ever need them to be async, because we might not need to
    // implement them in the frontend, so we're leaving this for now.
    loadStateValues(key: string): Record<string, unknown>;
    saveStateValues(key: string, values: Record<string, unknown>): void;
}

type ActionBackendWithHydrationContext = WireActionBackend & { readonly hydrationContext: ValueProviderActionContext };

function makeRowKeyForScreen(screenName: string, rowID: string): string {
    return `${screenName}-${rowID}`;
}

const makeBuilderNavigationModel = memoizeFunction(
    "makeBuilderNavigationModel",
    (
        currentLogicalTabIndex: number | "user-profile" | undefined,
        isCurrentTabVisible: boolean,
        screenName: string,
        screen: ScreenDescription | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        tabScreenName: string
    ): BuilderNavigationModel => {
        return {
            currentLogicalTabIndex,
            isCurrentTabVisible,
            currentScreenHasFlag: f => {
                if (f === ScreenFlag.IsUserProfileScreen) {
                    return screenName === userProfileScreenName;
                }
                if (screen === undefined) return f === ScreenFlag.IsErrorScreen || f === ScreenFlag.IsUnconfigurable;
                switch (f) {
                    case ScreenFlag.IsEditScreen:
                        return mutatingScreenKind === MutatingScreenKind.EditScreen;
                    case ScreenFlag.IsAddItemScreen:
                        return mutatingScreenKind === MutatingScreenKind.AddScreen;
                    case ScreenFlag.IsFormScreen:
                        return mutatingScreenKind === MutatingScreenKind.FormScreen;
                    case ScreenFlag.IsTopScreen:
                        return screenName === tabScreenName;
                    case ScreenFlag.IsArrayScreen:
                        return screen.kind === ScreenDescriptionKind.Array;
                    case ScreenFlag.IsMapScreen:
                    case ScreenFlag.IsUnconfigurable:
                    case ScreenFlag.IsSignaturePad:
                    case ScreenFlag.IsShareScreen:
                    case ScreenFlag.IsFinalTransient:
                    case ScreenFlag.IsErrorScreen:
                        return false;
                    default:
                        return assertNever(f);
                }
            },
        };
    }
);

const makeNavigationModelData = memoizeFunction(
    "makeNavigationModelData",
    (
        screenName: string,
        screen: ScreenDescription | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        context: InternalScreenContext | undefined,
        tables: InputOutputTables | undefined,
        navigationModel: BuilderNavigationModel
    ): NavigationModelData => {
        return {
            screenName,
            screen,
            mutatingScreenKind,
            navigationModel,
            context,
            tables,
        };
    }
);

export class WireBackendImpl implements WireBackend, WireBuilderBackend {
    private readonly navigationModelWatchable = new Watchable<WireNavigationModel>({
        urlPath: "/",
        appTitle: "",
        tabs: [],
        screen: emptyScreen,
        removeBranding: true,
    });
    private readonly metadataWatchable = new Watchable<NavigationModelData | undefined>(undefined);
    private parsedPath: ParsedPath | undefined;
    private lastParsedPath: ParsedPath | undefined;
    // This has to be brought in sync with `parsedPath` when recomputing.
    private internalNavigationModel: InternalNavigationModel | undefined;

    private maybeScreenInflator: ScreenInflator | undefined;

    private readonly invisibleRowIDs = new Map<string, string>();

    private readonly globalActions: ActionTokenMap = new Map();
    private readonly navigateToTabActions = new DefaultMap<string, WireAction>(tabScreenName => {
        const token = uuid();
        this.globalActions.set(token, [async ab => ab.navigateToTab(tabScreenName), emptyInternalScreenContext]);
        return { token };
    });
    private readonly showUserProfileAction: WireAction | undefined;
    private readonly signInAction: WireAction | undefined;
    private readonly signUpAction: WireAction | undefined;
    private readonly signOutAction: WireAction | undefined;
    private readonly closeModalAction: WireAction;

    private state = State.Clean;
    // TODO: This is only for debugging - remove
    private readonly id = uuid();

    constructor(
        public readonly appID: string,
        private readonly appFacilities: AppFacilities,
        private readonly dataStore: DataStore,
        private readonly context: ExistingAppDescriptionContext,
        private readonly callbacks: WireBackendCallbacks,
        public readonly isBuilder: boolean,
        urlPathOrPrevious: string | WireBackendImpl | undefined
    ) {
        logInfo("constructing", this.id);

        let parsedPath: ParsedPath | undefined;
        if (typeof urlPathOrPrevious === "string") {
            parsedPath = this.parseURLPath(urlPathOrPrevious);
        } else if (urlPathOrPrevious !== undefined) {
            parsedPath = urlPathOrPrevious.parsedPath;
            this.invisibleRowIDs = urlPathOrPrevious.invisibleRowIDs;
        }
        if (parsedPath === undefined || !this.isParsedPathValid(parsedPath)) {
            parsedPath = this.makeRootParsedPath();
        }

        this.parsedPath = parsedPath;

        const authData = getAppAuthenticationData(context.appDescription);
        if (authData.kind !== AuthenticationMethod.Disabled) {
            this.showUserProfileAction = { token: uuid() };
            this.globalActions.set(this.showUserProfileAction.token, [
                async ab => ab.navigateToUserProfile(),
                emptyInternalScreenContext,
            ]);

            this.signInAction = { token: uuid() };
            this.globalActions.set(this.signInAction.token, [
                async ab => {
                    ab.signIn();
                    return true;
                },
                emptyInternalScreenContext,
            ]);

            if (doesAuthenticationMethodAllowSignUp(authData.kind)) {
                this.signUpAction = this.signInAction;
            }

            this.signOutAction = { token: uuid() };
            this.globalActions.set(this.signOutAction.token, [
                async ab => {
                    ab.actionCallbacks.signOut();
                    return true;
                },
                emptyInternalScreenContext,
            ]);
        }

        this.closeModalAction = { token: uuid() };
        this.globalActions.set(this.closeModalAction.token, [
            async ab => {
                ab.closeScreen(PageScreenPosition.Modal);
                return true;
            },
            emptyInternalScreenContext,
        ]);

        dataStore.getComputationModelObservable(false).subscribe(this.onComputationModelChange);

        this.requestRecompute(true);
    }

    public get appKind(): AppKind {
        return AppKind.Page;
    }

    private get appDescription(): AppDescription {
        return this.context.appDescription;
    }

    public get wireNavigationModelObservable(): ChangeObservable<WireNavigationModel> {
        return this.navigationModelWatchable;
    }

    public get metadataObservable(): ChangeObservable<NavigationModelData | undefined> {
        return this.metadataWatchable;
    }

    private get computationModel(): ComputationModel | undefined {
        return this.dataStore.getComputationModelObservable(false).current;
    }

    private get screenInflator(): ScreenInflator | undefined {
        if (this.maybeScreenInflator === undefined) {
            const callbacks: ScreenInflatorCallbacks = {
                closeModalAction: this.closeModalAction,
                onDataChange: this.onDataChange,
                setColumnsInInvisibleRow: (tn, r, u) => this.setColumnsInInvisibleRow(tn, r, u),
                retireSubscriptionInfo: si => this.retireSubscriptionInfo(si),
            };
            this.maybeScreenInflator = new ScreenInflator(
                this.appID,
                this.appFacilities,
                this.dataStore,
                this.context,
                this.isBuilder,
                this.computationModel,
                callbacks
            );
        }
        return this.maybeScreenInflator;
    }

    // Will return `undefined` for the user profile faux-tab
    private findTab(tabScreenName: string): [TabDescription, number] | undefined {
        return findTabInAppDescription(tabScreenName, this.appDescription);
    }

    private makeMetadata(): NavigationModelData | undefined {
        const { parsedPath } = this;
        if (parsedPath === undefined) return undefined;

        let internalScreen: InternalScreen | undefined;
        let screenName: string | undefined;
        const [main, modal] = getParsedScreens(parsedPath);
        if (modal !== undefined) {
            screenName = modal.screenName;
            internalScreen = this.internalNavigationModel?.modal;
        } else {
            screenName = main.screenName;
            internalScreen = this.internalNavigationModel?.screen;
        }
        const { tabScreenName } = parsedPath;

        const maybeTab = this.findTab(tabScreenName);
        let currentLogicalTabIndex: number | undefined;
        let tabIsVisible: boolean;
        if (maybeTab !== undefined) {
            currentLogicalTabIndex = maybeTab[1];
            const internalTab = this.internalNavigationModel?.tabs?.[currentLogicalTabIndex];
            tabIsVisible = internalTab?.isVisible !== false;
        } else {
            tabIsVisible = true;
        }

        const screen: ScreenDescription | undefined = this.appDescription.screenDescriptions[screenName];
        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const navigationModel = makeBuilderNavigationModel(
            currentLogicalTabIndex ?? (parsedPath.tabScreenName === userProfileScreenName ? "user-profile" : undefined),
            tabIsVisible,
            screenName,
            screen,
            mutatingScreenKind,
            tabScreenName
        );
        let tables: InputOutputTables | undefined;
        if (isClassOrArrayScreenDescription(screen)) {
            tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => this.context.findTable(t));
        }
        return makeNavigationModelData(
            screenName,
            screen,
            mutatingScreenKind,
            internalScreen?.context,
            tables,
            navigationModel
        );
    }

    private unparseParsedPath(parsed: ParsedPath): string {
        return unparseParsedPath(parsed, this.appDescription);
    }

    private parseURLPath(urlPath: string): ParsedPath | undefined {
        return parseURLPath(urlPath, this.appDescription);
    }

    private isParsedPathValid(path: ParsedPath): boolean {
        return isParsedPathValid(path, this.appDescription);
    }

    private makeRootParsedPath(): ParsedPath | undefined {
        return makeRootParsedPath(this.appDescription);
    }

    private retireSubscriptionInfo(si: SubscriptionInfo): void {
        if (si.handlerAndPath !== undefined) {
            this.computationModel?.ns.deleteEntity(si.handlerAndPath.path);
        }
        for (const subsidiary of si.subsidiaries) {
            this.retireSubscriptionInfo(subsidiary);
        }
    }

    private retireHydratedScreen(hs: HydratedScreen): void {
        if (hs.screenContextSubscriptionInfo !== undefined) {
            this.retireSubscriptionInfo(hs.screenContextSubscriptionInfo);
        }
        for (const si of hs.componentStates) {
            this.retireSubscriptionInfo(si);
        }
        if (hs.titleSubscriptionInfo !== undefined) {
            this.retireSubscriptionInfo(hs.titleSubscriptionInfo);
        }
    }

    private retireScreen(screen: InternalScreen): void {
        const {
            context: { outputRow },
            hydratedScreen,
        } = screen;
        if (hydratedScreen !== undefined) {
            this.retireHydratedScreen(hydratedScreen);
        }
        if (outputRow !== undefined) {
            assert(!outputRow.$isVisible);

            const { computationModel } = this;
            if (computationModel === undefined) return;

            // FIXME: get table keeper and delete the invisible row
        }
    }

    public retire(): void {
        logInfo("retiring", this.id);

        assert(
            this.state !== State.Recomputing &&
                this.state !== State.FollowUpRequested &&
                this.state !== State.FollowUpWithCleanupRequested &&
                this.state !== State.Retired
        );
        this.state = State.Retired;

        this.dataStore.getComputationModelObservable(false).unsubscribe(this.onComputationModelChange);

        if (this.internalNavigationModel !== undefined) {
            this.retireScreen(this.internalNavigationModel.screen);
            if (this.internalNavigationModel.modal !== undefined) {
                this.retireScreen(this.internalNavigationModel.modal);
            }
        }
    }

    private loadStateValues(key: string | undefined): StateValuesMap | undefined {
        if (key === undefined) return undefined;
        const values = this.callbacks.loadStateValues(key);

        return new Map(Object.entries(values).map(([k, v]) => [k, [v, "DUMMY", x => x]]));
    }

    private saveStateValues(key: string | undefined, values: StateValuesMap): void {
        if (key === undefined) return;

        this.callbacks.saveStateValues(key, fromPairs(Array.from(values.entries()).map(([k, [v]]) => [k, v])));
    }

    private hydrateClassScreen(
        screen: ClassScreenDescription | undefined,
        hydrationContext: ScreenHydrationContext
    ): ScreenHydrationResult | undefined {
        const { internalScreen } = hydrationContext;
        const { hydratedScreen, screenName, defaultTitle } = internalScreen;

        const inflatedScreen = this.screenInflator?.inflateClassScreen(screenName, screen);
        if (inflatedScreen === undefined) return undefined;

        const { tables, componentHydrators, specialButtonHydrators, titleGetter, getScreenContext } = inflatedScreen;
        const [screenContext, screenContextSubscriptionInfo] = getScreenContext(
            hydrationContext,
            hydratedScreen?.screenContextSubscriptionInfo
        );

        let currentScreenTitle: string | undefined;

        const makeHydrationBackend = (cs: ComponentState | undefined, componentID: string | undefined) => {
            const stateSaveKey = makeStateSaveKey(componentID, screenContext.inputRow?.$rowID);
            const stateValues = definedMap(cs, s => s.stateValues) ?? this.loadStateValues(stateSaveKey);
            return new RowComponentHydrationBackend(
                hydrationContext,
                makeInternalScreenContext(screenContext.inputRow, screenContext.outputRow, undefined),
                stateValues,
                stateSaveKey,
                tables,
                currentScreenTitle
            );
        };

        const hydrateComponents = <THydrator>(
            hydrators: readonly [THydrator, string | undefined][],
            additionalInputs: readonly unknown[],
            hydrate: (hb: WireRowComponentHydrationBackend, h: THydrator) => WireComponentWithFlags | undefined,
            getHydrated: (hs: HydratedScreen) => [readonly ComponentState[], readonly (WireComponent | null)[]]
        ): [readonly (WireComponent | null)[], readonly ComponentState[]] => {
            const newComponents: (WireComponent | null)[] = [];
            const newComponentStates: ComponentState[] = [];

            function push(c: WireComponentWithFlags | undefined) {
                if (isArray(c)) {
                    c = c[0];
                }
                newComponents.push(c ?? null);
            }

            if (hydratedScreen !== undefined) {
                const [componentStates, wireComponents] = getHydrated(hydratedScreen);

                assert(componentStates.length === hydrators.length);
                assert(wireComponents.length === hydrators.length);

                const contextChanged = !areScreenContextsEqual(internalScreen.context, screenContext);

                for (const [hydratorWithID, cs, wc] of zip(hydrators, componentStates, wireComponents)) {
                    assert(cs !== undefined && wc !== undefined);
                    const [hydrator, componentID] = defined(hydratorWithID);
                    if (
                        contextChanged ||
                        isComponentStateDirty(cs, additionalInputs, hydrationContext.parsedPathChanged)
                    ) {
                        const hb = makeHydrationBackend(cs, componentID);
                        const component = hydrate(hb, hydrator);
                        push(component);
                        newComponentStates.push(hb.subscribeComponent(component, additionalInputs, this.onDataChange));

                        this.retireSubscriptionInfo(cs);
                    } else {
                        newComponents.push(wc);
                        newComponentStates.push(keepSubscriptionInfo(hydrationContext, hydratedScreen, cs));
                    }
                }
            } else {
                for (const [hydrator, componentID] of hydrators) {
                    const hb = makeHydrationBackend(undefined, componentID);
                    const component = hydrate(hb, hydrator);
                    push(component);
                    newComponentStates.push(hb.subscribeComponent(component, additionalInputs, this.onDataChange));
                }
            }

            assert(newComponents.length === hydrators.length);
            assert(newComponentStates.length === hydrators.length);

            return [newComponents, newComponentStates];
        };

        let titleSubscriptionInfo: SubscriptionInfo;
        if (
            hydratedScreen === undefined ||
            isSubscriptionDirty(hydratedScreen.titleSubscriptionInfo, hydrationContext.parsedPathChanged)
        ) {
            if (hydratedScreen !== undefined) {
                this.retireSubscriptionInfo(hydratedScreen.titleSubscriptionInfo);
            }
            const titleHB = makeHydrationBackend(undefined, undefined);
            currentScreenTitle = asMaybeString(nullToUndefined(titleGetter(titleHB))) ?? defaultTitle ?? "";
            titleSubscriptionInfo = titleHB.subscribeComponent(undefined, [], this.onDataChange);
        } else {
            currentScreenTitle = hydratedScreen.wireScreen.title;
            titleSubscriptionInfo = keepSubscriptionInfo(
                hydrationContext,
                hydratedScreen,
                hydratedScreen.titleSubscriptionInfo
            );
        }

        const [components, componentStates] = hydrateComponents(
            componentHydrators,
            [],
            (hb, h) => h(hb),
            hs => [hs.componentStates, hs.wireScreen.components]
        );
        const isEditValid =
            componentStates.some(s => s.hasValue) && componentStates.every(s => !s.isRequired || s.hasValue);
        const [untypedSpecialButtons, specialButtonStates] = hydrateComponents(
            specialButtonHydrators.map(h => [h, undefined]),
            [isEditValid],
            (hb, h) => h(hb, isEditValid),
            hs => [hs.specialButtonStates, hs.wireScreen.specialButtons]
        );
        const specialButtons = untypedSpecialButtons.map(c => {
            assert(c === null || c.kind === WireComponentKind.Button);
            return c as WireButtonComponent | null;
        });

        let closeAction: WireAction | undefined;
        if (hydrationContext.position !== PageScreenPosition.Main) {
            assert(hydrationContext.position === PageScreenPosition.Modal);
            closeAction = this.closeModalAction;
        }

        return {
            hydratedScreen: {
                actions: hydrationContext.actions,
                onChangeData: hydrationContext.onChangeValueRows,
                wireScreen: {
                    key: hydrationContext.screenKey,
                    title: currentScreenTitle,
                    components,
                    specialButtons,
                    closeAction,
                    flags: [],
                },
                screenContextSubscriptionInfo,
                componentStates,
                specialButtonStates,
                titleSubscriptionInfo,
            },
            context: screenContext,
        };
    }

    private hydrateArrayScreen(
        screen: ArrayScreenDescription,
        hydrationContext: ScreenHydrationContext
    ): ScreenHydrationResult | undefined {
        if (this.computationModel === undefined) return undefined;

        const { internalScreen } = hydrationContext;
        const { hydratedScreen, screenName } = internalScreen;

        const inflatedScreen = this.screenInflator?.inflateArrayScreen(screenName, screen);
        if (inflatedScreen === undefined) return undefined;

        const { table, contentHydrator, getContextTable } = inflatedScreen;

        const oldScreenContextSubscriptionInfo = hydratedScreen?.screenContextSubscriptionInfo;
        const maybeScreenContext = getContextTable(hydrationContext, oldScreenContextSubscriptionInfo);
        if (maybeScreenContext === undefined) return undefined;
        const [tableData, screenContextSubscriptionInfo] = maybeScreenContext;

        const makeHydrationBackend = () =>
            new TableComponentHydrationBackend(
                hydrationContext,
                Array.from(tableData.values()),
                makeInputOutputTables(table),
                undefined
            );

        const oldContentState = hydratedScreen?.componentStates[0];
        let contentComponentState: ComponentState;
        let component: WireComponent | undefined;
        if (
            oldContentState === undefined ||
            isComponentStateDirty(oldContentState, [], hydrationContext.parsedPathChanged) ||
            oldScreenContextSubscriptionInfo === undefined ||
            isSubscriptionDirty(oldScreenContextSubscriptionInfo, hydrationContext.parsedPathChanged)
        ) {
            const contentHydrationBackend = makeHydrationBackend();
            const componentWithFlags = contentHydrator(contentHydrationBackend, undefined);
            assert(!isArray(componentWithFlags));
            component = componentWithFlags;
            contentComponentState = contentHydrationBackend.subscribe(this.onDataChange);
        } else {
            assert(hydratedScreen !== undefined);
            component = nullToUndefined(hydratedScreen.wireScreen.components[0]);
            contentComponentState = keepSubscriptionInfo(hydrationContext, defined(hydratedScreen), oldContentState);
        }

        const components = definedMap(component, c => [c]) ?? [];
        const wireScreen: WireScreen = {
            key: hydrationContext.screenKey,
            components,
            specialButtons: [],
            // TODO: support title, once we support array screens
            title: internalScreen.defaultTitle ?? "",
            flags: [],
        };

        return {
            hydratedScreen: {
                actions: hydrationContext.actions,
                onChangeData: hydrationContext.onChangeValueRows,
                wireScreen,
                screenContextSubscriptionInfo,
                componentStates: [contentComponentState],
                specialButtonStates: [],
                titleSubscriptionInfo: {
                    handlerAndPath: undefined,
                    tokens: new Set(),
                    needsParsedPath: false,
                    subsidiaries: [],
                },
            },
            // TODO: We need a proper context, once we support array screens.
            context: emptyInternalScreenContext,
        };
    }

    private hydrateSpecialScreen(screenName: SpecialScreenName): ScreenHydrationResult {
        if (screenName === "sign-in") {
            const component: WireSignInComponent = {
                kind: WireComponentKind.SignIn,
                id: undefined,
                onSuccess: this.closeModalAction,
                onFailure: this.closeModalAction,
            };
            return {
                hydratedScreen: {
                    wireScreen: {
                        key: screenName,
                        title: "",
                        components: [component],
                        specialButtons: [],
                        closeAction: this.closeModalAction,
                        flags: [WireScreenFlag.IsSignIn],
                    },
                    screenContextSubscriptionInfo: undefined,
                    componentStates: [
                        {
                            handlerAndPath: undefined,
                            tokens: new Set(),
                            needsParsedPath: false,
                            subsidiaries: [],
                            stateValues: new Map(),
                            stateSaveKey: undefined,
                            stateChanged: false,
                            isRequired: false,
                            hasValue: false,
                            additionalInputs: [],
                        },
                    ],
                    specialButtonStates: [],
                    titleSubscriptionInfo: {
                        handlerAndPath: undefined,
                        tokens: new Set(),
                        needsParsedPath: false,
                        subsidiaries: [],
                    },
                    actions: new Map(),
                    onChangeData: new Map(),
                },
                context: emptyInternalScreenContext,
            };
        } else {
            return assertNever(screenName);
        }
    }

    private hydrateScreen(hydrationContext: ScreenHydrationContext): ScreenHydrationResult | undefined {
        const { screenName } = hydrationContext.internalScreen;

        if (isSpecialScreenName(screenName)) {
            return this.hydrateSpecialScreen(screenName);
        }

        const screen = this.appDescription.screenDescriptions[screenName] as ScreenDescription | undefined;

        if (
            screen?.kind === ScreenDescriptionKind.Class ||
            (screen === undefined && screenName === userProfileScreenName)
        ) {
            return this.hydrateClassScreen(screen, hydrationContext);
        } else if (screen?.kind === ScreenDescriptionKind.Array) {
            return this.hydrateArrayScreen(screen, hydrationContext);
        } else if (screen?.kind === ScreenDescriptionKind.Chat || screen?.kind === ScreenDescriptionKind.ShoppingCart) {
            return undefined;
        } else {
            assert(screen === undefined);
            return undefined;

            // TS 4.1 doesn't support this, 4.3 does:
            //
            // if (screen === undefined) return undefined;
            // return assertNever(screen);
        }
    }

    private hydrateUserProfile(
        tablesToFetch: ArraySet<TableName>
    ): [WireUserProfile | undefined, SubscriptionInfo | undefined] {
        const inflated = this.screenInflator?.inflateUserProfile();
        if (inflated === undefined || this.parsedPath === undefined) return [undefined, undefined];

        const vp = new RowHydrationValueProvider(
            {
                namespace: this.computationModel?.ns,
                fetchTableData: tn => tablesToFetch.add(tn),
                actions: new Map(),
                parsedPath: this.parsedPath,
            },
            undefined,
            undefined,
            undefined
        );

        let userProfile: WireUserProfile | undefined;
        const email = asMaybeString(nullToUndefined(inflated.emailValueGetter(vp)));
        if (email !== undefined) {
            const name = asMaybeString(nullToUndefined(inflated.nameValueGetter?.(vp)));
            const parsedPath = vp.getParsedPath();
            let userProfileScreenIsOpen: boolean;
            if (parsedPath === undefined) {
                userProfileScreenIsOpen = false;
            } else {
                userProfileScreenIsOpen = parsedPath.tabScreenName === userProfileScreenName;
            }
            userProfile = {
                userProfileButton: {
                    signedInUserEmail: email,
                    signedInUserName: name,
                    userProfileAction: this.showUserProfileAction,
                    userProfileScreenIsOpen,
                },
                signOutAction: this.signOutAction,
            };
        } else {
            userProfile = {
                signInAction: this.signInAction,
                signUpAction: this.signUpAction,
            };
        }

        return [userProfile, vp.subscribe(this.onDataChange)];
    }

    // This makes sure that all the dirt makes its way up to the subscription
    // handlers.
    private recomputeSubscriptionHandlers(): void {
        const process = (si: SubscriptionInfo | undefined) => {
            if (si?.handlerAndPath !== undefined) {
                this.computationModel?.ns.get(si.handlerAndPath.path);
            }
            for (const subsidiary of si?.subsidiaries ?? []) {
                process(subsidiary);
            }
        };

        const processScreen = (screen: InternalScreen | undefined) => {
            if (screen?.hydratedScreen === undefined) return;

            process(screen.hydratedScreen.screenContextSubscriptionInfo);
            for (const si of screen.hydratedScreen.componentStates) {
                process(si);
            }
        };

        processScreen(this.internalNavigationModel?.screen);
        processScreen(this.internalNavigationModel?.modal);
        for (const tab of this.internalNavigationModel?.tabs ?? []) {
            process(tab.visibilitySubscriptionInfo);
        }

        process(this.internalNavigationModel?.userProfileSubscriptionInfo);
    }

    private getParsedScreenDescription(parsedScreen: ParsedScreen): ClassScreenDescription | undefined {
        const screen = this.appDescription.screenDescriptions[parsedScreen.screenName];
        if (screen?.kind !== ScreenDescriptionKind.Class) return undefined;
        return screen;
    }

    // `false` means the screen fetches its own row
    private getRowForParsedScreen(parsedScreen: ParsedScreen): Row | false | undefined {
        const screen = this.getParsedScreenDescription(parsedScreen);
        if (screen === undefined) return undefined;

        if (parsedScreen.rowID === undefined) {
            if (screen.fetchesData === true) {
                return false;
            } else {
                return undefined;
            }
        }

        const table = this.computationModel?.getBaseDataForTable(getTableName(getTableRefTableName(screen.type)));
        return table?.get(parsedScreen.rowID);
    }

    private makeInternalScreen(parsedScreen: ParsedScreen): InternalScreen {
        const { screenName } = parsedScreen;
        const screen = this.getParsedScreenDescription(parsedScreen);

        const tab = this.findTab(screenName)?.[0];

        let context: InternalScreenContext | undefined;
        const row = this.getRowForParsedScreen(parsedScreen);
        if (parsedScreen.rowID !== undefined) {
            if (row !== undefined && row !== false) {
                const key = makeRowKeyForScreen(screenName, parsedScreen.rowID);
                if (screen !== undefined && isFormScreen(screen)) {
                    const outputTableName = getTableRefTableName(screen.formType);
                    const outputRow = this.getOrAddEmptyRow(outputTableName, key);
                    context = makeInternalScreenContext(row, outputRow, undefined);
                } else if (screen !== undefined && isEditClassScreenName(screenName)) {
                    const outputTableName = getTableRefTableName(screen.type);
                    const outputRow = this.getOrCopyRowWithNewRowID(
                        outputTableName,
                        {
                            ...row,
                            $rowID: makeRowID(),
                            $isVisible: false,
                        },
                        key
                    );
                    context = makeInternalScreenContext(row, outputRow, undefined);
                } else if (screen !== undefined && isAddClassScreenName(screenName)) {
                    // FIXME: How do we have an input row in an Add screen?
                    const outputTableName = getTableRefTableName(screen.type);
                    const outputRow = this.getOrAddEmptyRow(outputTableName, key);
                    context = makeInternalScreenContext(undefined, outputRow, undefined);
                } else {
                    context = makeInternalScreenContext(row, undefined, undefined);
                }
            }
        }

        return {
            screenName,
            defaultTitle: tab?.title,
            context: context ?? emptyInternalScreenContext,
            hydratedScreen: undefined,
        };
    }

    private makeInternalModalScreen(parsedScreen: ParsedModalScreen): InternalModalScreen {
        const internalScreen = this.makeInternalScreen(parsedScreen);
        assert(internalScreen.hydratedScreen === undefined);
        return { ...internalScreen, hydratedScreen: undefined, size: parsedScreen.size };
    }

    private updateInternalScreenGeneric<P extends ParsedScreen, I extends InternalScreen>(
        parsedScreen: P,
        internalScreen: I | undefined,
        makeScreen: (p: P) => I
    ): I {
        const retireAndMakeDefault = () => {
            if (internalScreen !== undefined) {
                this.retireScreen(internalScreen);
            }
            return makeScreen(parsedScreen);
        };

        if (internalScreen === undefined || parsedScreen.screenName !== internalScreen.screenName) {
            return retireAndMakeDefault();
        }

        const row = this.getRowForParsedScreen(parsedScreen);
        if (row !== false && row !== internalScreen.context.inputRow) {
            return retireAndMakeDefault();
        }

        return internalScreen;
    }

    private updateInternalScreen(
        parsedScreen: ParsedScreen,
        internalScreen: InternalScreen | undefined
    ): InternalScreen {
        return this.updateInternalScreenGeneric(parsedScreen, internalScreen, p => this.makeInternalScreen(p));
    }

    private updateInternalModalScreen(
        parsedScreen: ParsedModalScreen,
        internalScreen: InternalModalScreen | undefined
    ): InternalModalScreen {
        return this.updateInternalScreenGeneric(parsedScreen, internalScreen, p => this.makeInternalModalScreen(p));
    }

    private readonly lastWireScreen = new Map<PageScreenPosition, WireScreen>();
    private compareWireScreen(position: PageScreenPosition, screen: WireScreen): void {
        const lastWireScreen = this.lastWireScreen.get(position);
        if (lastWireScreen !== undefined) {
            const l = Math.max(lastWireScreen.components.length, screen.components.length);
            let numDiff = 0;
            for (let i = 0; i < l; i++) {
                if (lastWireScreen.components[i] !== screen.components[i]) {
                    numDiff++;
                }
            }
            logInfo(`screen components differ ${numDiff}/${l}`);
        }
        this.lastWireScreen.set(position, screen);
    }

    private getScreenTitle(screen: ParsedScreen, hb: WireRowComponentHydrationBackend): string | null | undefined {
        const { screenInflator } = this;
        if (screenInflator === undefined) return undefined;

        const [getter, tables] = screenInflator.inflateScreenTitle(screen.screenName);
        let row = this.getRowForParsedScreen(screen);
        if (row === false) {
            const screenDesc = this.context.appDescription.screenDescriptions[screen.screenName];
            if (screenDesc.kind === ScreenDescriptionKind.Class) {
                const common = screenInflator.inflateClassScreenCommon(screen.screenName, screenDesc);
                if (common !== undefined) {
                    assert(hb instanceof RowHydrationValueProvider);
                    const screenContext = screenInflator.fetchScreenData(screen.screenName, screenDesc, hb.context);
                    if (screenContext !== undefined) {
                        const [{ inputRow }, si] = screenContext;
                        row = inputRow;
                        hb.addSubscriptionInfo(si);
                    }
                }
            }
            if (row === false) {
                row = undefined;
            }
        }

        const rowHB = hb.makeHydrationBackendForRow(row, tables);

        const v = getter(rowHB);
        if (v === null || v === undefined) return v;
        return asString(v);
    }

    private rebuildScreen(
        internalScreen: InternalScreen,
        position: PageScreenPosition,
        tablesToFetch: ArraySet<TableName>,
        internalTabs: readonly InternalTab[] | undefined,
        screenKey: string,
        parsedPathChanged: boolean
    ): InternalScreen {
        const visibleTabScreenNames = new Map(internalTabs?.map(t => [t.tabScreenName, t.visibilityPredicate]));

        let parsedPath = defined(this.parsedPath);
        if (position !== PageScreenPosition.Modal) {
            parsedPath = parsedPathRemoveModals(parsedPath);
        }
        const hydrationContext: ScreenHydrationContext = {
            namespace: this.computationModel?.ns,
            internalScreen,
            fetchTableData: tn => tablesToFetch.add(tn),
            actions: new Map(),
            onChangeValueRows: new Map(),
            position,
            tabScreenVisibilityPredicates: visibleTabScreenNames,
            verifiedEmailAddressPath: this.computationModel?.getVerifiedEmailAddressPath(),
            parsedPath,
            screenKey,
            parsedPathChanged,
            getScreenTitle: (s, hb) => this.getScreenTitle(s, hb),
        };
        // `hydrateScreen` is responsible for retiring everything that needs
        // to be retired, unless it fails, in which case it really should
        // never have succeeded.
        const result = this.hydrateScreen(hydrationContext);
        if (result === undefined) {
            if (internalScreen.hydratedScreen !== undefined) {
                this.retireHydratedScreen(internalScreen.hydratedScreen);
            }
            return internalScreen;
        }
        this.compareWireScreen(position, result.hydratedScreen.wireScreen);
        return { ...internalScreen, context: result.context, hydratedScreen: result.hydratedScreen };
    }

    private rebuildModalScreen(
        internalScreen: InternalModalScreen,
        position: PageScreenPosition,
        tablesToFetch: ArraySet<TableName>,
        internalTabs: readonly InternalTab[] | undefined,
        screenKey: string,
        parsedPathChanged: boolean
    ): InternalModalScreen {
        const result = this.rebuildScreen(
            internalScreen,
            position,
            tablesToFetch,
            internalTabs,
            screenKey,
            parsedPathChanged
        );
        return {
            ...result,
            hydratedScreen: definedMap(
                result.hydratedScreen,
                (h: HydratedScreen): HydratedModalScreen => ({
                    ...h,
                    wireScreen: { ...h.wireScreen, size: internalScreen.size },
                })
            ),
            size: internalScreen.size,
        };
    }

    private computeTabVisibility(
        tablesToFetch: ArraySet<TableName>,
        parsedPathChanged: boolean
    ): readonly InternalTab[] | undefined {
        const { screenInflator } = this;
        if (screenInflator === undefined || this.parsedPath === undefined) return undefined;

        const hydrationContext: ValueProviderActionContext = {
            namespace: this.computationModel?.ns,
            fetchTableData: tn => tablesToFetch.add(tn),
            // TODO: There should maybe be a value provider that
            // doesn't do actions, for just evaluating predicates like
            // here.
            actions: new Map(),
            parsedPath: this.parsedPath,
        };

        function makeInternalTab(hydrator: InternalTabHydrator): InternalTab {
            const hb = new RowHydrationValueProvider(hydrationContext, undefined, undefined, undefined);
            return hydrator(hb);
        }

        const inflatedTabs = screenInflator.inflateInternalTabs();
        if (this.internalNavigationModel?.tabs === undefined) {
            return inflatedTabs.map(makeInternalTab);
        } else {
            return this.internalNavigationModel.tabs.map(it => {
                if (!isSubscriptionDirty(it.visibilitySubscriptionInfo, parsedPathChanged)) {
                    return it;
                }
                this.retireSubscriptionInfo(it.visibilitySubscriptionInfo);

                const maybeTab = this.findTab(it.tabScreenName);
                if (maybeTab === undefined) {
                    // There's nothing we can do here, so we just don't
                    // re-hydrate.
                    return it;
                }

                const [, tabIndex] = maybeTab;
                const hydrator = defined(inflatedTabs[tabIndex]);

                return makeInternalTab(hydrator);
            });
        }
    }

    private updateParsedPathWithTabVisibility(internalTabs: readonly InternalTab[] | undefined): void {
        if (this.isBuilder) return;

        // If we couldn't evaluate internal tabs that means the app doesn't
        // have a user profile table or something else missing, so we just
        // ignore visibility.
        if (internalTabs === undefined || this.parsedPath === undefined) return;

        const appTabs = getAppTabs(this.appDescription);
        assert(internalTabs.length === appTabs.length);

        const invisibleRootScreens = new Set<string>();
        let defaultTab: TabDescription | undefined;
        let defaultTabScreenName: string | undefined;
        for (const [tab, internalTab] of zip(appTabs, internalTabs)) {
            const visible = internalTab?.isVisible !== false;

            if (visible) {
                if (defaultTab === undefined) {
                    defaultTab = tab;
                    defaultTabScreenName = getScreenProperty(tab?.screenName);
                }
            } else {
                invisibleRootScreens.add(defined(internalTab).tabScreenName);
            }
        }

        function process<T extends ParsedScreen>(screen: T | undefined): T | undefined {
            if (screen === undefined) return undefined;
            if (invisibleRootScreens.has(screen.screenName)) return undefined;
            return screen;
        }

        let rootScreen = process(this.parsedPath.rootScreen);
        if (rootScreen === undefined) {
            // If there's no default tab then no tab is visible, so we're
            // resetting the parsed path.
            //
            // TODO: Allow for restoring the parsed path when tabs become
            // visible again.
            if (defaultTab === undefined || defaultTabScreenName === undefined) {
                this.parsedPath = undefined;
                return;
            }

            rootScreen = {
                kind: PageScreenPosition.Main,
                screenName: defaultTabScreenName,
                rowID: undefined,
            };
        }

        let tabScreenName = this.parsedPath.tabScreenName;
        if (invisibleRootScreens.has(tabScreenName)) {
            if (defaultTabScreenName === undefined) {
                this.parsedPath = undefined;
                return;
            }
            tabScreenName = defaultTabScreenName;
        }

        const screenStack = mapFilterUndefined(this.parsedPath.screenStack, process);

        this.parsedPath = {
            rootScreen,
            screenStack,
            tabScreenName,
        };
    }

    private makeScreenKeys(): [string, string] | undefined {
        const { parsedPath: path } = this;
        if (path === undefined) return undefined;

        const [main, modal] = getParsedScreens(path);

        const modalIndex = path.screenStack.findIndex(s => s.kind === PageScreenPosition.Modal);
        const mainDepth = modalIndex < 0 ? path.screenStack.length : modalIndex;
        const modalDepth = path.screenStack.length;
        return [
            `${unparseParsedScreen(main).join("/")}-${mainDepth}`,
            definedMap(modal, s => `${unparseParsedScreen(s).join("/")}-${modalDepth}`) ?? "",
        ];
    }

    // TODO: We hydrate twice if the user changes the data in an entry
    // component. The first render is caused by a push of some dirt, and then
    // the second render is triggered during the first render when we `get`
    // all the computation model entities we're subscribed to.  The latter is
    // necessary because without it the computation model doesn't even know
    // yet which rows/columns will get dirtied (because it hasn't computed
    // them yet).  It is a bit wasteful though, since the first render will be
    // superseded right away.  It would be nice if instead of the first render
    // we'd just go over all the subscription infos and `get` them, at which
    // point we will know exactly what has changed and we can rehydrate that
    // just once.  Right now this is not a big issue, but it will be when/if
    // we separate the display layer from the component model via an actual
    // wire.
    private rebuildNavigationModel(): void {
        if (this.internalNavigationModel !== undefined) {
            assert(this.parsedPath !== undefined);
        }
        if (this.parsedPath === undefined) {
            assert(this.internalNavigationModel === undefined);
            return;
        }

        this.recomputeSubscriptionHandlers();

        const tablesToFetch = new ArraySet<TableName>(areTableNamesEqual);
        const parsedPathChanged = !deepEqual(this.parsedPath, this.lastParsedPath, { strict: true });

        // 1. Compute tab visibility.
        const internalTabs = this.computeTabVisibility(tablesToFetch, parsedPathChanged);

        // 2. Update the parsed path to not show invisible tab root screens,
        //    and to not have an invisible tab selected.
        this.updateParsedPathWithTabVisibility(internalTabs);

        // 3. Update the internal navigation model to reflect the parsed path.
        const [main, modal] = getParsedScreens(this.parsedPath);
        if (this.internalNavigationModel === undefined) {
            this.internalNavigationModel = {
                screen: this.makeInternalScreen(main),
                modal: definedMap(modal, p => this.makeInternalModalScreen(p)),
                tabs: undefined,
                userProfile: {
                    userProfileButton: undefined,
                    signInAction: undefined,
                },
                userProfileSubscriptionInfo: undefined,
            };
        } else {
            this.internalNavigationModel = {
                screen: this.updateInternalScreen(main, this.internalNavigationModel.screen),
                modal: definedMap(modal, m => this.updateInternalModalScreen(m, this.internalNavigationModel?.modal)),
                tabs: this.internalNavigationModel.tabs,
                userProfile: this.internalNavigationModel.userProfile,
                userProfileSubscriptionInfo: this.internalNavigationModel.userProfileSubscriptionInfo,
            };
        }

        const wireTabs: WireTab[] = [];

        const appTabs = getAppTabs(this.appDescription);

        for (const [appTab, internalTab] of zip(appTabs, internalTabs ?? [])) {
            assert(appTab !== undefined);

            if (appTab.showInMenu === false || internalTab?.isVisible === false) continue;

            const tabScreenName = getScreenProperty(appTab.screenName);
            if (tabScreenName === undefined) continue;

            const wireTab: WireTab = {
                title: appTab.title ?? "",
                isActive: tabScreenName === this.parsedPath.tabScreenName,
                action: this.navigateToTabActions.get(tabScreenName),
            };

            wireTabs.push(wireTab);
        }

        // 4. Recompute screens.
        let userProfile: WireUserProfile | undefined;
        let userProfileSubscriptionInfo: SubscriptionInfo | undefined;
        if (
            this.internalNavigationModel.userProfileSubscriptionInfo === undefined ||
            isSubscriptionDirty(this.internalNavigationModel.userProfileSubscriptionInfo, parsedPathChanged)
        ) {
            if (this.internalNavigationModel.userProfileSubscriptionInfo !== undefined) {
                this.retireSubscriptionInfo(this.internalNavigationModel.userProfileSubscriptionInfo);
            }

            const hydrated = this.hydrateUserProfile(tablesToFetch);
            userProfile = hydrated[0];
            userProfileSubscriptionInfo = hydrated[1];
        } else {
            userProfile = this.internalNavigationModel.userProfile;
            userProfileSubscriptionInfo = this.internalNavigationModel.userProfileSubscriptionInfo;
        }

        const [mainKey, modalKey] = defined(this.makeScreenKeys());
        this.internalNavigationModel = {
            screen: this.rebuildScreen(
                this.internalNavigationModel.screen,
                PageScreenPosition.Main,
                tablesToFetch,
                internalTabs,
                mainKey,
                parsedPathChanged
            ),
            modal: definedMap(this.internalNavigationModel.modal, m =>
                this.rebuildModalScreen(
                    m,
                    PageScreenPosition.Modal,
                    tablesToFetch,
                    internalTabs,
                    modalKey,
                    parsedPathChanged
                )
            ),
            tabs: internalTabs,
            userProfile,
            userProfileSubscriptionInfo,
        };

        const navModel: WireNavigationModel = {
            urlPath: this.unparseParsedPath(this.parsedPath),
            appTitle: this.appDescription.title,
            appTitleAction: wireTabs[0]?.action,
            tabs: wireTabs.length > 1 ? wireTabs : [],
            screen: this.internalNavigationModel.screen.hydratedScreen?.wireScreen ?? emptyScreen,
            modal: this.internalNavigationModel.modal?.hydratedScreen?.wireScreen,
            userProfileButton: this.internalNavigationModel.userProfile?.userProfileButton,
            signInAction: this.internalNavigationModel.userProfile?.signInAction,
            signUpAction: this.internalNavigationModel.userProfile?.signUpAction,
            signOutAction: this.internalNavigationModel.userProfile?.signOutAction,
            removeBranding: false,
            iconImage: this.appDescription.iconImage,
        };

        this.navigationModelWatchable.current = maybeShallowOldest(this.navigationModelWatchable.current, navModel);
        this.metadataWatchable.current = maybeShallowOldest(this.metadataWatchable.current, this.makeMetadata());
        this.lastParsedPath = this.parsedPath;

        for (const tableName of tablesToFetch.values()) {
            this.dataStore.fetchTableRows(tableName, false);
        }
    }

    private recompute(): void {
        if (this.state === State.Retired) return;

        logInfo("recomputing", this.id);

        assert(this.state === State.Requested);
        this.state = State.Recomputing;
        try {
            this.rebuildNavigationModel();
        } finally {
            if ((this.state as State) === State.FollowUpRequested) {
                this.state = State.Clean;
                this.onDataChange();
                return;
            } else if ((this.state as State) === State.FollowUpWithCleanupRequested) {
                this.state = State.Clean;
                this.cleanUpInflated();
                this.onDataChange();
                return;
            } else {
                assert(this.state === State.Recomputing);
                this.state = State.Clean;
            }
        }
    }

    private pushScreen(
        screenBase: ParsedScreenBase,
        target: PageScreenTarget,
        // `source === undefined` means go to the tab root.  In that case
        // `target` must be `PageScreenPosition.Main`.
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): boolean {
        const { parsedPath } = this;
        if (parsedPath === undefined) return false;

        let isMain: boolean;
        let modalSize = WireModalSize.Small;
        if (target === PageScreenTarget.Current) {
            isMain = source !== PageScreenPosition.Modal;
            modalSize = sourceModalSize ?? modalSize;
        } else if (target === PageScreenTarget.Main) {
            isMain = true;
        } else if (target === PageScreenTarget.SmallModal) {
            isMain = false;
        } else if (target === PageScreenTarget.LargeModal) {
            isMain = false;
            modalSize = WireModalSize.Large;
        } else {
            isMain = proveNever(target, "Probably legacy", true);
        }

        let screen: ParsedScreen;
        if (isMain) {
            screen = { kind: PageScreenPosition.Main, ...screenBase };
        } else {
            screen = { kind: PageScreenPosition.Modal, size: modalSize, ...screenBase };
        }

        this.parsedPath = parsedPathPush(parsedPath, screen);

        return true;
    }

    private runPushFreeScreen(
        screenName: string,
        target: PageScreenTarget,
        row: Row | undefined,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): void {
        this.pushScreen({ screenName, rowID: row?.$rowID }, target, source, sourceModalSize);
    }

    private runPushDefaultClassScreen(
        tableName: TableName,
        row: Row,
        target: PageScreenTarget,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): void {
        this.pushScreen({ screenName: classScreenName(tableName), rowID: row.$rowID }, target, source, sourceModalSize);
    }

    private runPushFormScreen(
        screenName: string,
        row: Row,
        target: PageScreenTarget,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): void {
        const key = makeRowKeyForScreen(screenName, row.$rowID);
        this.invisibleRowIDs.delete(key);
        this.pushScreen({ screenName, rowID: row.$rowID }, target, source, sourceModalSize);
    }

    private runPushDefaultEditScreen(
        tableName: TableName,
        row: Row,
        target: PageScreenTarget,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): void {
        const screenName = editClassScreenName(tableName);
        const key = makeRowKeyForScreen(screenName, row.$rowID);
        this.invisibleRowIDs.delete(key);
        this.pushScreen({ screenName, rowID: row.$rowID }, target, source, sourceModalSize);
    }

    private runPushSpecialScreen(
        screenName: SpecialScreenName,
        target: PageScreenTarget,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): void {
        this.pushScreen(
            {
                screenName,
                rowID: undefined,
            },
            target,
            source,
            sourceModalSize
        );
    }

    private runCloseScreen(position: PageScreenPosition.Modal): void {
        if (this.parsedPath === undefined) return;

        if (position === PageScreenPosition.Modal) {
            this.parsedPath = parsedPathCloseModal(this.parsedPath);
        } else {
            return assertNever(position);
        }
    }

    private runNavigateToPath(path: ParsedPath): boolean {
        if (!this.isParsedPathValid(path)) return false;

        this.parsedPath = path;
        return true;
    }

    private runNavigateToTab(tabScreenNameOrLogicalTabIndex: string | number, force: boolean): boolean {
        if (tabScreenNameOrLogicalTabIndex === userProfileScreenName) {
            this.parsedPath = {
                tabScreenName: userProfileScreenName,
                rootScreen: { kind: PageScreenPosition.Main, screenName: userProfileScreenName, rowID: undefined },
                screenStack: [],
            };
        } else {
            if (typeof tabScreenNameOrLogicalTabIndex === "string") {
                const result = this.findTab(tabScreenNameOrLogicalTabIndex);
                if (result === undefined) return false;
                tabScreenNameOrLogicalTabIndex = result[1];
            }

            const internalTab = this.internalNavigationModel?.tabs?.[tabScreenNameOrLogicalTabIndex];
            if (internalTab === undefined) return false;
            if (!internalTab.isVisible && !force) return false;

            const { tabScreenName } = internalTab;

            this.parsedPath = {
                tabScreenName,
                rootScreen: { kind: PageScreenPosition.Main, screenName: tabScreenName, rowID: undefined },
                screenStack: [],
            };
        }
        return true;
    }

    public navigateToVisibleTab(): void {
        if (this.internalNavigationModel?.tabs === undefined) return;

        // Find the first tab whose visibility conditition is true.
        for (const it of this.internalNavigationModel?.tabs) {
            if (!it.isVisible) continue;
            const maybeTab = this.findTab(it.tabScreenName);
            if (maybeTab === undefined) continue;
            const [, index] = maybeTab;
            this.navigateToTabRoot(index);
            return;
        }
    }

    public navigateToTabRoot(tabScreenNameOrLogicalTabIndex: string | number): void {
        this.runNavigateToTab(tabScreenNameOrLogicalTabIndex, true);
        this.requestRecompute(false);
    }

    private getCurrentPageScreenPosition(): [PageScreenPosition, WireModalSize | undefined] {
        if (this.parsedPath === undefined) return [PageScreenPosition.Main, undefined];
        const [, modal] = getParsedScreens(this.parsedPath);

        let source: PageScreenPosition;
        let sourceModalSize: WireModalSize | undefined;
        if (modal !== undefined) {
            source = PageScreenPosition.Modal;
            sourceModalSize = modal.size;
        } else {
            source = PageScreenPosition.Main;
        }
        return [source, sourceModalSize];
    }

    public pushFakeScreen(screenName: string): void {
        const screen = this.appDescription.screenDescriptions[screenName];
        if (screen === undefined) return;

        const [source, sourceModalSize] = this.getCurrentPageScreenPosition();

        if (screen.kind === ScreenDescriptionKind.Class) {
            if (!isSingleRelationType(screen.type)) return;
            const path = this.computationModel?.getBasePathForTable(getTableRefTableName(screen.type));
            if (path === undefined) return;
            const table = definedMap(this.computationModel?.ns.get(path), asTable);
            if (table === undefined) return;
            const rowID = Array.from(table.keys())[0];
            this.pushScreen(
                {
                    screenName,
                    rowID,
                },
                PageScreenTarget.Current,
                source,
                sourceModalSize
            );
        } else if (screen.kind === ScreenDescriptionKind.Array) {
            this.pushScreen({ screenName, rowID: undefined }, PageScreenTarget.Current, source, sourceModalSize);
        }
        this.requestRecompute(false);
    }

    public navigateToUserProfile(): void {
        this.runNavigateToTab(userProfileScreenName, true);
        this.requestRecompute(false);
    }

    public popToLogicalTab(): void {
        if (this.parsedPath === undefined) return;

        this.parsedPath = {
            tabScreenName: this.parsedPath.tabScreenName,
            rootScreen: {
                kind: PageScreenPosition.Main,
                screenName: this.parsedPath.tabScreenName,
                rowID: undefined,
            },
            screenStack: [],
        };

        this.requestRecompute(false);
    }

    private async runSetColumnsInRow(
        tableName: TableName,
        row: Row,
        updates: Record<string, GroundValue>
    ): Promise<boolean> {
        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const rowIndex = getRowIndexForRow(table, row);
        if (rowIndex === undefined) return false;

        await this.dataStore.setColumnsInRow(
            getTableName(table),
            rowIndex,
            Data.fromJSON<AppOther, AppEnvironment>(updates),
            true,
            true,
            false
        );

        return true;
    }

    private async runDeleteRow(tableName: TableName, row: Row): Promise<boolean> {
        if (!row.$isVisible) return false;

        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const rowIndex = getRowIndexForRow(table, row);
        if (rowIndex === undefined) return false;

        await this.dataStore.deleteRowAtIndex(tableName, rowIndex, true, false);

        return true;
    }

    private async runAddRow(tableName: TableName, r: Row): Promise<boolean> {
        const outputTable = this.context.findTable(tableName);
        if (outputTable === undefined) return false;

        assert(!r.$isVisible);

        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        keeper?.deleteInvisibleRow(r.$rowID);

        const newRow: Record<string, GroundValue> = {};
        const columnNames = new Set<string>();
        for (const c of outputTable.columns) {
            if (isComputedColumn(c)) continue;
            if (!isPrimitiveType(c.type)) continue;

            const v = getRowColumn(r, c.name);
            if (v === undefined) continue;

            newRow[c.name] = v;
            columnNames.add(c.name);
        }
        if (outputTable.rowIDColumn !== undefined) {
            newRow[outputTable.rowIDColumn] = r.$rowID;
        }

        const result = await this.dataStore.addRowToTable(
            tableName,
            Data.fromJSON<AppOther, AppEnvironment>(newRow),
            columnNames,
            true,
            false
        );
        return result.didAdd;
    }

    private async runSaveEditScreenChanges(tableName: TableName, sc: InternalScreenContext): Promise<boolean> {
        assert(sc.inputRow !== undefined && sc.outputRow !== undefined);

        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const updates: Record<string, GroundValue> = {};

        for (const column of table.columns) {
            if (isComputedColumn(column)) continue;
            if (!isPrimitiveType(column.type)) continue;
            if (column.name === table.rowIDColumn) continue;
            if (column.name === "$rowID") continue;

            const inputValue = getRowColumn(sc.inputRow, column.name);
            const outputValue = getRowColumn(sc.outputRow, column.name);
            if (inputValue === outputValue) continue;

            updates[column.name] = outputValue;
        }

        return await this.runSetColumnsInRow(tableName, sc.inputRow, updates);
    }

    private getOrCopyRowWithNewRowID(tableName: TableName, row: Row, key: string): Row | undefined {
        const outputTable = this.context.findTable(tableName);
        if (outputTable === undefined) return undefined;

        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        if (keeper === undefined) return undefined;

        const rowID = this.invisibleRowIDs.get(key);
        if (rowID !== undefined) {
            const tableData = this.computationModel?.getBaseDataForTable(tableName);
            const existingRow = tableData?.get(rowID);
            if (existingRow?.$isVisible === false) {
                return existingRow;
            }
        }

        const copy: Row = { ...row, $rowID: makeRowID() };
        if (outputTable.rowIDColumn !== undefined) {
            copy[outputTable.rowIDColumn] = copy.$rowID;
        }

        keeper.addInvisibleRow(copy);
        this.invisibleRowIDs.set(key, copy.$rowID);

        return copy;
    }

    private getOrAddEmptyRow(tableName: TableName, key: string): Row | undefined {
        return this.getOrCopyRowWithNewRowID(tableName, { $rowID: "", $isVisible: false }, key);
    }

    private setColumnsInInvisibleRow(tableName: TableName, row: Row, update: Record<string, GroundValue | null>): void {
        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        if (keeper === undefined) return;

        // TODO: We're kinda abusing this API.  I'm not sure it's
        // meant to let you update invisible rows without bothering
        // the data store.
        keeper.updateRowWithPartialData(
            { keyColumnName: "$rowID", keyColumnValue: row.$rowID },
            removeNullFromObject(update)
        );
    }

    private requestRecompute(sync: boolean): void {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);
        if (
            this.state === State.Requested ||
            this.state === State.FollowUpRequested ||
            this.state === State.FollowUpWithCleanupRequested
        )
            return;
        this.state = State.Requested;

        if (sync) {
            this.recompute();
        } else {
            setTimeout(() => this.recompute(), 0);
        }
    }

    private makeActionBackend(
        rowScreenContext: InternalRowContext | undefined,
        source: PageScreenPosition | undefined,
        sourceModalSize: WireModalSize | undefined
    ): ActionBackendWithHydrationContext | undefined {
        if (this.computationModel === undefined || this.parsedPath === undefined) return undefined;

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const backend = this;

        class ActionBackend extends RowHydrationValueProvider<ValueProviderActionContext> implements WireActionBackend {
            constructor(public readonly hydrationContext: ValueProviderActionContext) {
                super(hydrationContext, rowScreenContext, undefined, undefined);
            }

            public get appID(): string {
                return backend.appID;
            }

            public get actionCallbacks(): WireActionCallbacks {
                return backend.callbacks;
            }

            public getState<T>(
                _name: string,
                _validate: (v: unknown) => v is T,
                _defaultValue: T
            ): WireAlwaysEditableValue<T> {
                return panic("State is not supported in actions");
            }

            public pushFreeScreen(screenName: string, target: PageScreenTarget, row: Row | undefined): void {
                backend.runPushFreeScreen(screenName, target, row, source, sourceModalSize);
            }

            public pushDefaultClassScreen(tableName: TableName, r: Row, target: PageScreenTarget): void {
                backend.runPushDefaultClassScreen(tableName, r, target, source, sourceModalSize);
            }

            public pushDefaultEditScreen(tableName: TableName, r: Row, target: PageScreenTarget): void {
                backend.runPushDefaultEditScreen(tableName, r, target, source, sourceModalSize);
            }

            public pushFormScreen(screenName: string, r: Row, target: PageScreenTarget): void {
                backend.runPushFormScreen(screenName, r, target, source, sourceModalSize);
            }

            public navigateToUserProfile(): boolean {
                return backend.runNavigateToTab(userProfileScreenName, false);
            }

            public closeScreen(position: PageScreenPosition.Modal): void {
                backend.runCloseScreen(position);
            }

            public navigateToPath(path: ParsedPath): boolean {
                return backend.runNavigateToPath(path);
            }

            public navigateToTab(tabScreenName: string): boolean {
                return backend.runNavigateToTab(tabScreenName, false);
            }

            public async addRow(tableName: TableName, r: Row): Promise<boolean> {
                return await backend.runAddRow(tableName, r);
            }

            public async setColumnsInRow(
                tableName: TableName,
                r: Row,
                updates: Record<string, GroundValue>
            ): Promise<boolean> {
                return await backend.runSetColumnsInRow(tableName, r, updates);
            }

            public async deleteRow(tableName: TableName, r: Row): Promise<boolean> {
                return await backend.runDeleteRow(tableName, r);
            }

            public async saveEditScreenChanges(tableName: TableName, sc: InternalScreenContext): Promise<boolean> {
                return await backend.runSaveEditScreenChanges(tableName, sc);
            }

            public signIn(): void {
                if (backend.isBuilder) {
                    // We never do sign-in in the builder.  We allow the
                    // action to happen, but we open the user profile screen.
                    // Maybe that will confuse users.
                    this.navigateToUserProfile();
                } else {
                    backend.runPushSpecialScreen("sign-in", PageScreenTarget.SmallModal, source, sourceModalSize);
                }
            }

            public valueChanged(token: string, value: PrimitiveValue): void {
                backend.valueChangedInternal(token, value);
            }
        }

        const context: ValueProviderActionContext = {
            namespace: this.computationModel.ns,
            fetchTableData: () => undefined,
            // This isn't actually needed, but our types demand it
            actions: new Map(),
            parsedPath: this.parsedPath,
        };

        return new ActionBackend(context);
    }

    private async runActionWithRunnerAndRequestRecompute(
        ab: ActionBackendWithHydrationContext,
        runner: WireActionRunner
    ): Promise<void> {
        await runner(ab);
        assert(ab.hydrationContext.actions.size === 0);
        this.requestRecompute(false);
    }

    public runAction(token: string, _handled: boolean): void {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);

        if (this.internalNavigationModel === undefined) return;

        let action = this.internalNavigationModel.screen.hydratedScreen?.actions.get(token);
        let source: PageScreenPosition = PageScreenPosition.Main;
        let sourceModalSize: WireModalSize | undefined;
        if (action === undefined && this.internalNavigationModel.modal !== undefined) {
            action = this.internalNavigationModel.modal.hydratedScreen?.actions.get(token);
            source = PageScreenPosition.Modal;
            sourceModalSize = this.internalNavigationModel.modal.size;
        }
        if (action === undefined) {
            action = this.globalActions.get(token);
            source = this.getCurrentPageScreenPosition()[0];
        }
        if (action === undefined) return;

        const [runner, row] = action;

        const ab = this.makeActionBackend(row, source, sourceModalSize);
        if (ab === undefined) return;

        void this.runActionWithRunnerAndRequestRecompute(ab, runner);
    }

    private async runValueChanged(entry: OnChangeData, value: PrimitiveValue): Promise<void> {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);

        if (entry.kind === "column") {
            const { tableName, columnName, row } = entry;

            const update = { [columnName]: value };

            if (row.$isVisible) {
                await this.runSetColumnsInRow(tableName, row, update);
            } else {
                this.setColumnsInInvisibleRow(tableName, row, update);
            }
        } else {
            return assertNever(entry.kind);
        }
    }

    // Returns whether we need to trigger a recomputation ourselves.  If we
    // change data in the computation model, it'll update our subscriptions,
    // so in that case we don't have to worry.
    private valueChangedInternal(token: string, value: PrimitiveValue): boolean {
        if (this.internalNavigationModel === undefined) return false;

        const processScreen = (hydratedScreen: HydratedScreen | undefined): boolean => {
            if (hydratedScreen === undefined) return false;

            const entry = hydratedScreen.onChangeData.get(token);
            if (entry !== undefined) {
                void this.runValueChanged(entry, value);
                return false;
            }

            for (const cs of hydratedScreen.componentStates ?? []) {
                for (const [k, [, t, c]] of cs.stateValues) {
                    if (token === t) {
                        cs.stateValues.set(k, [c(value), t, c]);
                        cs.stateChanged = true;
                        this.saveStateValues(cs.stateSaveKey, cs.stateValues);
                        return true;
                    }
                }
            }

            return false;
        };

        return (
            processScreen(this.internalNavigationModel.screen.hydratedScreen) ||
            processScreen(this.internalNavigationModel.modal?.hydratedScreen)
        );
    }

    public valueChanged(token: string, value: PrimitiveValue): void {
        if (this.valueChangedInternal(token, value)) {
            this.requestRecompute(false);
        }
    }

    public urlPathChanged(urlPath: string): void {
        if (this.parsedPath !== undefined && this.unparseParsedPath(this.parsedPath) === normalizeURLPath(urlPath)) {
            return;
        }

        const parsed = this.parseURLPath(urlPath);
        if (parsed === undefined) return;

        this.parsedPath = parsed;
        this.requestRecompute(false);
    }

    // This is where we ##cleanUpInflatedStuff.
    private cleanUpInflated(): void {
        assert(this.state === State.Clean || this.state === State.Requested);
        this.internalNavigationModel = undefined;
        this.maybeScreenInflator = undefined;
    }

    private readonly onComputationModelChange = (): void => {
        if (this.state === State.FollowUpWithCleanupRequested) {
            return;
        }
        if (this.state === State.Recomputing || this.state === State.FollowUpRequested) {
            this.state = State.FollowUpWithCleanupRequested;
            return;
        }

        this.cleanUpInflated();

        this.onDataChange();
    };

    private readonly onDataChange = (): void => {
        if (this.state === State.Recomputing) {
            this.state = State.FollowUpRequested;
            return;
        }

        if (this.state !== State.Clean) return;
        this.state = State.Requested;

        logInfo("onDataChange", this.id);

        // It would be nice if we didn't have to do this async.  Also, make
        // sure we're only doing this once per change.  Might have to modify
        // the namespace to only call it when it has finished recomputing.
        setTimeout(() => this.recompute(), 0);
    };
}
